
import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  schema?: Record<string, unknown>;
  image?: string;
  type?: string;
}

const DEFAULT_IMAGE = 'https://elpasowastedisposal.com/og-image.jpg';
const SITE_NAME = 'El Paso Waste Disposal';

export function useSEO({ 
  title, 
  description, 
  keywords, 
  canonical, 
  schema,
  image = DEFAULT_IMAGE,
  type = 'website'
}: SEOProps) {
  useEffect(() => {
    // Set page title
    document.title = title;
    
    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    
    // Set meta tags
    const metaTags = [
      { name: 'description', content: description },
      { name: 'last-modified', content: today },
      ...(keywords ? [{ name: 'keywords', content: keywords }] : []),
      // Geo tags for local SEO
      { name: 'geo.region', content: 'US-TX' },
      { name: 'geo.placename', content: 'El Paso, Texas' },
      { name: 'geo.position', content: '31.7619;-106.4850' },
      { name: 'ICBM', content: '31.7619, -106.4850' },
      // Open Graph tags
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: type },
      { property: 'og:site_name', content: SITE_NAME },
      { property: 'og:locale', content: 'en_US' },
      { property: 'og:image', content: image },
      ...(canonical ? [{ property: 'og:url', content: canonical }] : []),
      // Twitter Card tags
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title.length > 70 ? title.substring(0, 67) + '...' : title },
      { name: 'twitter:description', content: description.length > 200 ? description.substring(0, 197) + '...' : description },
      { name: 'twitter:image', content: image }
    ];

    metaTags.forEach(tag => {
      const attribute = tag.name ? 'name' : 'property';
      const value = tag.name || tag.property;
      let element = document.querySelector(`meta[${attribute}="${value}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, value!);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });

    // Set canonical link
    if (canonical) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonical);
    }

    // Set schema.org JSON-LD
    if (schema) {
      // Remove existing schema scripts first
      const existingSchemas = document.querySelectorAll('script[type="application/ld+json"][data-seo="true"]');
      existingSchemas.forEach(el => el.remove());
      
      const schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.setAttribute('data-seo', 'true');
      schemaScript.textContent = JSON.stringify(schema);
      document.head.appendChild(schemaScript);
    }

    // Cleanup function
    return () => {
      const seoSchemas = document.querySelectorAll('script[type="application/ld+json"][data-seo="true"]');
      seoSchemas.forEach(el => el.remove());
    };
  }, [title, description, keywords, canonical, schema, image, type]);
}
